import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "@/lib/toast";
import { Trash2 } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Table, StatusBadge, Field, Select, SecondaryButton, SortableHeader, useTableSort } from "@/components/admin/AdminUI";
import { formatDate } from "@/lib/format";
import { ModalScroll } from "@/components/public/ModalScroll";

export default function AdminContacts() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { data: items = [] } = useQuery({
    queryKey: ["contacts", statusFilter],
    queryFn: async () => (await api.get("/admin/contact-submissions", { params: { status: statusFilter || undefined } })).data,
  });
  const { sort, toggleSort, sortRows } = useTableSort();
  const sortedItems = sortRows(items, (c, key) => {
    if (key === "subject") return c.subject || c.message?.slice(0, 60) || "";
    if (key === "created_at") return c.created_at ? new Date(c.created_at).getTime() : null;
    return c[key] ?? null;
  });

  async function setStatus(id, status) {
    await api.patch(`/admin/contact-submissions/${id}`, { status });
    qc.invalidateQueries({ queryKey: ["contacts"] });
  }

  async function clearContacts() {
    const scope = statusFilter ? `'${statusFilter}'` : "ALL";
    const msg = `Delete ${items.length} ${scope} contact submission${items.length === 1 ? "" : "s"}?\n\nThis only clears messages submitted via the public Contact page. Label-mismatch / false-ad reports (on the Reports page) will NOT be touched.`;
    if (!window.confirm(msg)) return;
    setDeleting(true);
    try {
      const { data } = await api.delete("/admin/contact-submissions", {
        params: { status: statusFilter || undefined },
      });
      toast.success(`Cleared ${data.deleted} contact${data.deleted === 1 ? "" : "s"}.`);
      qc.invalidateQueries({ queryKey: ["contacts"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear contacts");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Contact submissions"
        subtitle={`${items.length} submissions`}
        actions={
          items.length > 0 && (
            <SecondaryButton
              onClick={clearContacts}
              disabled={deleting}
              data-testid="clear-contacts-button"
              className="text-clay hover:text-clay/80 border-clay/30"
            >
              <Trash2 className="w-4 h-4 inline mr-1" />
              {deleting ? "Clearing…" : statusFilter ? `Clear ${statusFilter}` : "Clear contacts"}
            </SecondaryButton>
          )
        }
      />
      <Card className="mb-4">
        <Field label="Filter status">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="responded">Responded</option>
            <option value="archived">Archived</option>
          </Select>
        </Field>
      </Card>
      <div className="bg-white border border-[#E9ECEF] rounded-lg overflow-hidden">
        <Table>
          <thead className="bg-[#F8F9FA] border-b border-[#E9ECEF] text-xs uppercase tracking-wider text-[#6C757D]">
            <tr>
              <SortableHeader label="From" sortKey="name" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Category" sortKey="category" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Subject" sortKey="subject" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Status" sortKey="status" sort={sort} onSort={toggleSort} />
              <SortableHeader label="Received" sortKey="created_at" sort={sort} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((c) => (
              <tr key={c.id} className="border-b border-[#E9ECEF] cursor-pointer hover:bg-[#F8F9FA]" onClick={() => { setOpen(c); if (c.status === "new") setStatus(c.id, "read"); }}>
                <td className="px-3 py-3">{c.name}<br /><span className="text-xs text-[#6C757D]">{c.email}</span></td>
                <td className="px-3 py-3 text-xs">{c.category}</td>
                <td className="px-3 py-3 text-sm max-w-xs truncate">{c.subject || c.message?.slice(0, 60)}</td>
                <td className="px-3 py-3"><StatusBadge status={c.status} /></td>
                <td className="px-3 py-3 text-xs">{formatDate(c.created_at)}</td>
              </tr>
            ))}
            {sortedItems.length === 0 && <tr><td colSpan={5} className="px-3 py-12 text-center text-[#6C757D]">No submissions.</td></tr>}
          </tbody>
        </Table>
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white max-w-xl w-full h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 pt-5 pb-3 border-b border-[#E9ECEF]">
              <div className="flex items-start justify-between gap-3">
                <h3 className="font-semibold text-lg">{open.subject || "(no subject)"}</h3>
                <StatusBadge status={open.status} />
              </div>
              <div className="text-xs text-[#6C757D] mt-1">{open.name} &lt;{open.email}&gt; · {open.category} · {formatDate(open.created_at)}</div>
            </div>

            <ModalScroll className="px-6 py-4">
              {open.brand_url && (
                <div className="mb-4 p-3 bg-[#F8F9FA] border border-[#E9ECEF]">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Brand&apos;s website (suggested)</div>
                  <a href={open.brand_url} target="_blank" rel="noopener noreferrer" className="text-sm text-forest underline break-all">{open.brand_url}</a>
                </div>
              )}
              {open.item_url_on_site && (
                <div className="mb-4 p-3 bg-[#F8F9FA] border border-[#E9ECEF]">
                  <div className="text-[10px] uppercase tracking-wider text-[#6C757D] mb-1">Item on this website</div>
                  <a href={open.item_url_on_site} target="_blank" rel="noopener noreferrer" className="text-sm text-forest underline break-all">
                    {open.clothing_item_name || open.item_url_on_site}
                  </a>
                  {open.brand_name && (
                    <div className="text-[11px] text-[#6C757D] mt-1">Brand: {open.brand_name}</div>
                  )}
                </div>
              )}

              <p className="text-sm whitespace-pre-wrap mb-4">{open.message}</p>
              {open.file_urls?.length > 0 && (
                <div className="mb-4 grid grid-cols-3 gap-2">
                  {open.file_urls.map((u, i) => <a key={u} href={u} target="_blank" rel="noopener noreferrer"><img src={u} alt="" className="aspect-square object-cover" /></a>)}
                </div>
              )}
            </ModalScroll>

            <div className="border-t border-[#E9ECEF] px-6 py-3">
              <div className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">Set status</div>
              <div className="flex flex-wrap gap-2" data-testid="contact-status-actions">
                {["new", "read", "responded", "archived"].map((s) => (
                  <SecondaryButton
                    key={s}
                    disabled={open.status === s}
                    onClick={async () => {
                      await setStatus(open.id, s);
                      setOpen((o) => o ? { ...o, status: s } : o);
                    }}
                    data-testid={`contact-set-${s}`}
                    className={open.status === s ? "ring-2 ring-forest" : ""}
                  >
                    {s[0].toUpperCase() + s.slice(1)}
                  </SecondaryButton>
                ))}
              </div>
              {open.email && (
                <a
                  href={`mailto:${open.email}?subject=Re:%20${encodeURIComponent(open.subject || "Your message")}`}
                  className="inline-block mt-3 text-sm text-forest underline underline-offset-4"
                >
                  ✉ Reply by email
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
